import { dateFormatOutput } from '@/const.js';
import { formatDate } from '@/util.js';
import calendarTable from '@components/calendar/calendar-table/component.js';
import { inputTypeTime } from '@components/input/index.js';
import renders from './render.js';

export default (componentNode) => {
  if (!componentNode) {
    return false;
  }

  calendarTable({
    selectors: {
      component: '[data-schedule-calendar="component"]',
      monthTitle: '[data-schedule-calendar="month"]',
      yearTitle: '[data-schedule-calendar="year"]',
      slider: '[data-schedule-calendar="calendar-slider"]',
      sliderPrevBtn: '[data-schedule-calendar="prev-month"]',
      sliderNextBtn: '[data-schedule-calendar="next-month"]',
    },
  });

  const initialState = {
    windowVisibility: 'hidden',
    selectMode: 'single',
    timeFromValue: '',
    timeToValue: '',
    days: [],
    timeFormatValidity: 'idle',
  };

  let state = {
    ...initialState,
  };

  // global.state = state;

  inputTypeTime(componentNode.querySelector('[data-schedule-calendar="time-from"]'));
  inputTypeTime(componentNode.querySelector('[data-schedule-calendar="time-to"]'));

  const addNewDay = (addingDate) => {
    const { days, selectMode } = state;

    const newDay = {
      date: addingDate,
      timeFromValue: '',
      timeToValue: '',
      focusingState: 'focused',
      fillingState: 'unfilled',
    };

    if (selectMode === 'single') {
      state.days = days
        .filter(({ fillingState }) => fillingState === 'filled')
        .map((day) => ({
          ...day,
          focusingState: 'unfocused',
        }))
        .concat(newDay);
    }

    if (selectMode === 'multiple') {
      state.days = days
        .map((day) => {
          const { fillingState } = day;

          if (fillingState === 'filled') {
            return {
              ...day,
              focusingState: 'unfocused',
            };
          }

          return {
            ...day,
            focusingState: 'focused',
          };
        })
        .concat(newDay);
    }

    renders.renderDaysActivity(state, componentNode);
    renders.renderDaysFocusing(state, componentNode);
    renders.renderTimeStage(state, componentNode);
  };

  const removeDay = (removingDate) => {
    const { days, selectMode } = state;
    const newDays = days.filter(({ date }) => date !== removingDate);

    if (selectMode === 'single') {
      state.days = newDays
        .filter(({ fillingState }) => fillingState === 'filled')
        .map((day) => ({
          ...day,
          focusingState: 'unfocused',
        }));
    }

    if (selectMode === 'multiple') {
      state.days = [...newDays];
    }

    renders.renderDayTime(state, componentNode);
    renders.renderDaysActivity(state, componentNode);
    renders.renderDaysFocusing(state, componentNode);
    renders.renderDaysFilling(state, componentNode);
    renders.renderTimeStage(state, componentNode);
    renders.renderApplyButton(state, componentNode);
    renders.renderCancelButton(state, componentNode);
  };

  const focusDay = (focusingDay) => {
    const { days, selectMode } = state;

    const {
      date: focusingDate,
    } = focusingDay;

    state.days = days
      .filter(({ fillingState }) => fillingState === 'filled')
      .map((day) => {
        const { date } = day;

        if (date === focusingDate) {
          return {
            ...day,
            focusingState: 'focused',
          };
        }

        if (selectMode === 'single') {
          return {
            ...day,
            focusingState: 'unfocused',
          };
        }

        return day;
      });

    renders.renderDaysActivity(state, componentNode);
    renders.renderDaysFocusing(state, componentNode);
    renders.renderTimeStage(state, componentNode);
  };

  const unfocusAllDays = () => {
    const { days } = state;

    state.days = days.map((day) => ({
      ...day,
      focusingState: 'unfocused',
    }));

    renders.renderDaysFocusing(state, componentNode);
    renders.renderTimeStage(state, componentNode);
  };

  const setDayTime = () => {
    const { days, timeFromValue, timeToValue } = state;

    state.days = days.map((day) => {
      const { focusingState } = day;

      if (focusingState !== 'focused') {
        return day;
      }

      return {
        ...day,
        fillingState: 'filled',
        timeFromValue,
        timeToValue,
      };
    });

    renders.renderDayTime(state, componentNode);
    renders.renderDaysFilling(state, componentNode);
    renders.renderApplyButton(state, componentNode);
    renders.renderCancelButton(state, componentNode);
  };

  const removeDayTime = () => {
    const { days } = state;
    const daysToRemove = days.filter(({ focusingState }) => focusingState === 'focused');

    daysToRemove.forEach(({ date }) => {
      removeDay(date);
    });
  };

  const validateTime = () => {
    const { timeFromValue, timeToValue } = state;
    const timeRegExp = /^(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/;
    const hasTimeFormatErrors = (!timeFromValue.match(timeRegExp)
      || !timeToValue.match(timeRegExp));
    state.timeFormatValidity = 'idle';

    if (hasTimeFormatErrors) {
      state.timeFormatValidity = 'invalid';
      renders.renderTimeStageErrors(state, componentNode);
      return false;
    }

    renders.renderTimeStageErrors(state, componentNode);
    return true;
  };

  const removeTime = () => {
    state.timeFromValue = '';
    state.timeToValue = '';

    renders.renderTimeFields(state, componentNode);
  };

  const setTime = (from, to) => {
    state.timeFromValue = from;
    state.timeToValue = to;

    renders.renderTimeFields(state, componentNode);
  };

  const hideWindow = () => {
    state.windowVisibility = 'hidden';
    renders.renderWindowVisibility(state, componentNode);
  };

  const showWindow = () => {
    state.windowVisibility = 'shown';
    renders.renderWindowVisibility(state, componentNode);
  };

  const prepareDaysData = () => {
    const { days } = state;
    const initialAcc = {
      date: [],
      timeStart: [],
      timeEnd: [],
    };

    return days
      .filter(({ fillingState }) => fillingState === 'filled')
      .reduce((acc, day) => {
        const { date, timeFromValue, timeToValue } = day;
        const formatted = formatDate(date, dateFormatOutput);
        return {
          date: [...acc.date, formatted],
          timeStart: [...acc.timeStart, timeFromValue],
          timeEnd: [...acc.timeEnd, timeToValue],
        };
      }, initialAcc);
  };

  const setResult = () => {
    const result = prepareDaysData();
    renders.renderResult(result, componentNode);
  };

  const reset = () => {
    state = {
      ...initialState,
    };

    renders.renderDayTime(state, componentNode);
    renders.renderDaysActivity(state, componentNode);
    renders.renderDaysFocusing(state, componentNode);
    renders.renderDaysFilling(state, componentNode);
    renders.renderTimeStage(state, componentNode);
    renders.renderApplyButton(state, componentNode);
    renders.renderCancelButton(state, componentNode);

    setResult();
  };

  componentNode.addEventListener('click', (event) => {
    const dayNode = event.target.closest('.calendar-table__day');

    if (!dayNode) {
      return;
    }

    state.selectMode = event.shiftKey ? 'multiple' : 'single';
    const { days, selectMode } = state;

    const hasFocusedDaysGroup = (days.filter(({ focusingState }) => focusingState === 'focused')).length > 1;
    const activeDayDate = dayNode.dataset.calendarDate;
    const [day] = days.filter(({ date }) => date === activeDayDate);

    if (!day) {
      addNewDay(activeDayDate);
      removeTime();
      return;
    }

    const {
      fillingState,
      focusingState,
      timeFromValue,
      timeToValue,
    } = day;

    if (focusingState !== 'focused') {
      focusDay(day);

      if (fillingState === 'filled') {
        setTime(timeFromValue, timeToValue);
      }
      return;
    }

    removeDay(activeDayDate);

    if (hasFocusedDaysGroup && selectMode === 'single') {
      addNewDay(activeDayDate);
    }
  });

  componentNode.addEventListener('click', (event) => {
    const applyTimeNode = event.target.closest('[data-schedule-calendar="time-apply"]');

    if (!applyTimeNode) {
      return;
    }

    const isTimeValid = validateTime();

    if (!isTimeValid) {
      return;
    }

    setDayTime();
    unfocusAllDays();
    removeTime();
  });

  componentNode.addEventListener('click', (event) => {
    const resetTimeNode = event.target.closest('[data-schedule-calendar="time-reset"]');

    if (!resetTimeNode) {
      return;
    }

    removeDayTime();
    removeTime();
  });

  componentNode.addEventListener('change', (event) => {
    const timeFromNode = event.target.closest('[data-schedule-calendar="time-from"]');

    if (!timeFromNode) {
      return;
    }

    state.timeFromValue = timeFromNode.value;
  });

  componentNode.addEventListener('change', (event) => {
    const timeToNode = event.target.closest('[data-schedule-calendar="time-to"]');

    if (!timeToNode) {
      return;
    }

    state.timeToValue = timeToNode.value;
  });

  componentNode.addEventListener('click', (event) => {
    const applyButtonNode = event.target.closest('[data-schedule-calendar="apply-btn"]');

    if (!applyButtonNode) {
      return;
    }

    setResult();
    hideWindow();
  });

  componentNode.addEventListener('click', (event) => {
    const cancelButtonNode = event.target.closest('[data-schedule-calendar="cancel-btn"]');

    if (!cancelButtonNode) {
      return;
    }

    const { days } = state;
    const filledDays = days.filter(({ fillingState }) => fillingState === 'filled');

    if (filledDays.length) {
      filledDays.forEach(({ date }) => {
        removeDay(date);
      });

      setResult();
      return;
    }

    setResult();
    hideWindow();
  });

  componentNode.addEventListener('click', (event) => {
    const backButtonNode = event.target.closest('[data-schedule-calendar="back-btn"]');

    if (!backButtonNode) {
      return;
    }

    setResult();
    hideWindow();
  });

  componentNode.addEventListener('click', (event) => {
    const togglerNode = event.target.closest('[data-schedule-calendar="toggler"]');

    if (!togglerNode) {
      return;
    }

    const { windowVisibility } = state;

    if (windowVisibility === 'shown') {
      hideWindow();
      return;
    }

    showWindow();
  });

  return {
    reset,
  };
};
